<template>
    <div class="register-container">
        <!-- Animated background elements -->
        <div class="animated-bg">
            <div class="gradient-sphere sphere-1"></div>
            <div class="gradient-sphere sphere-2"></div>
            <div class="gradient-sphere sphere-3"></div>
        </div>

        <v-container>
            <v-card class="register-card" elevation="24">
                <v-row no-gutters>
                    <!-- Form Section -->
                    <v-col cols="12" lg="6" md="6" sm="12" class="form-section">
                        <div class="form-content">
                            <!-- Logo with enhanced animation -->
                            <div class="logo-wrapper">
                                <img :src="logo" class="logo-img" loading="lazy" alt="Locinder Logo" />
                            </div>

                            <h1>Join the movement of Locinder</h1>

                            <p class="subtitle">A food discovery app for local business in Sagay City</p>

                            <!-- Step Indicator -->
                            <div class="step-indicator">
                                <div v-for="step in steps" :key="step.number" class="step-item" :class="{
                                    active: currentStep === step.number,
                                    completed: currentStep > step.number
                                }" @click="goToStep(step.number)">
                                    <div class="step-circle">
                                        <v-icon v-if="currentStep > step.number" size="16"
                                            color="white">mdi-check</v-icon>
                                        <span v-else>{{ step.number }}</span>
                                    </div>
                                    <span class="step-label">{{ step.label }}</span>
                                </div>
                            </div>

                            <v-form ref="form" @submit.prevent="handleRegister" v-model="isFormValid"
                                class="register-form">
                                <!-- Step 1: Business Info -->
                                <div v-show="currentStep === 1" class="step-content mb-10" key="step1">
                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-account-outline" size="18" class="label-icon" />
                                            <span>Owner's Full Name</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.shop_owner" 
                                            :rules="[requiredRule]"
                                            placeholder="Juan Dela Cruz" 
                                            variant="outlined" 
                                            density="compact"
                                            class="custom-input" />
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-storefront-outline" size="18" class="label-icon" />
                                            <span>Store / Business Name</span>
                                        </div>
                                        <v-text-field
                                            v-model="formData.shop_name"
                                            :rules="[requiredRule, max50Rule]"
                                            placeholder="Kapehan ni Toto"
                                            variant="outlined"
                                            density="compact"
                                            class="custom-input"
                                            maxlength="50"
                                            counter="50"
                                        />
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-domain" size="18" class="label-icon" />
                                            <span>Store Type</span>
                                        </div>
                                        <v-select 
                                            :items="storeTypes"
                                            v-model="formData.shop_type"
                                            :rules="[requiredRule]" 
                                            placeholder="Select store type" 
                                            variant="outlined"
                                            density="compact" 
                                            class="custom-input" />
                                    </div>
                                </div>

                                <!-- Step 2: Location & Hours -->
                                <div v-show="currentStep === 2" class="step-content mb-10" key="step2">
                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-map-marker-outline" size="18" class="label-icon" />
                                            <span>Store Address</span>
                                        </div>
                                        <v-textarea 
                                            v-model="formData.shop_address"
                                            :rules="[requiredRule]"
                                            placeholder="Purok Paghidaet, Brgy. Paraiso, Sagay City"
                                            variant="outlined" 
                                            density="compact" 
                                            rows="1" 
                                            class="custom-input" />
                                        <div style="position: relative; margin-top: -18px; float: right; font-size: 11px !important;">
                                            <v-chip @click="showLocationConfirmDialog" color="success" size="small">
                                                <v-icon color="success">mdi-map-marker-outline</v-icon>&nbsp;Use current location
                                            </v-chip>
                                        </div>
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-clock-outline" size="18" class="label-icon" />
                                            <span>Open Hour</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.open_at"
                                            :rules="[requiredRule, timeFormatRule]"
                                            placeholder="07:00 AM" 
                                            variant="outlined" 
                                            density="compact"
                                            class="custom-input"
                                            maxlength="8"
                                            counter="8" />
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-clock-outline" size="18" class="label-icon" />
                                            <span>Close Hour</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.close_at"
                                            :rules="[requiredRule, timeFormatRule]" 
                                            placeholder="08:00 PM"
                                            variant="outlined" 
                                            density="compact" 
                                            class="custom-input"
                                            maxlength="8"
                                            counter="8" />
                                    </div>
                                </div>

                                <!-- Step 3: Contact & Auth -->
                                <div v-show="currentStep === 3" class="step-content" key="step3">
                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-phone-outline" size="18" class="label-icon" />
                                            <span>Mobile Number</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.shop_contact_number"
                                            :rules="[requiredRule, mobileNumberRule]" 
                                            placeholder="0912 345 6789"
                                            variant="outlined" 
                                            density="compact" 
                                            class="custom-input" />
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-email-outline" size="18" class="label-icon" />
                                            <span>Email Address</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.shop_email"
                                            :rules="[requiredRule, emailFormatRule]" 
                                            placeholder="admin@example.com"
                                            variant="outlined" 
                                            density="compact" 
                                            class="custom-input" />
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-lock-outline" size="18" class="label-icon" />
                                            <span>Password</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.admin_password"
                                            :rules="[requiredRule, passwordRule]" 
                                            placeholder="Create a strong password"
                                            variant="outlined" 
                                            density="compact"
                                            class="custom-input"
                                            :type="showPassword ? 'text' : 'password'">
                                            <template v-slot:append-inner>
                                                <v-icon :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                                    @click="showPassword = !showPassword" class="cursor-pointer" />
                                            </template>
                                        </v-text-field>
                                    </div>

                                    <div class="input-wrapper">
                                        <div class="input-label">
                                            <v-icon icon="mdi-lock-check-outline" size="18" class="label-icon" />
                                            <span>Confirm Password</span>
                                        </div>
                                        <v-text-field 
                                            v-model="formData.confirm_password"
                                            :rules="[requiredRule, confirmPasswordRule]"
                                            placeholder="Confirm your password" 
                                            variant="outlined" 
                                            density="compact"
                                            class="custom-input"
                                            :type="showConfirmPassword ? 'text' : 'password'">
                                            <template v-slot:append-inner>
                                                <v-icon :icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                                                    @click="showConfirmPassword = !showConfirmPassword"
                                                    class="cursor-pointer" />
                                            </template>
                                        </v-text-field>
                                    </div>

                                    <div class="input-wrapper">
                                        <v-checkbox v-model="acceptTerms" color="orange" class="terms-checkbox" hide-details>
                                            <template #label>
                                                <p>
                                                    <span class="text-grey-darken-2">I accept the</span>
                                                    <span class="terms-link" @click.stop="openTermsSheet">
                                                        Terms of Use
                                                    </span>
                                                </p>
                                            </template>
                                        </v-checkbox>
                                    </div>
                                </div>

                                <!-- Navigation Buttons -->
                                <div class="navigation-buttons">
                                    <v-btn v-if="currentStep > 1" variant="outlined" size="large"
                                        class="nav-btn prev-btn" @click="prevStep" :disabled="loading">
                                        <v-icon left>mdi-arrow-left</v-icon>
                                        &nbsp; Previous
                                    </v-btn>

                                    <v-btn v-if="currentStep < totalSteps" color="primary" size="large"
                                        class="nav-btn next-btn" @click="nextStep" :disabled="!isStepValid || loading">
                                        Next&nbsp;
                                        <v-icon right>mdi-arrow-right</v-icon>
                                    </v-btn>

                                    <v-btn v-if="currentStep === totalSteps" color="primary" type="submit" size="large"
                                        class="nav-btn register-btn" :loading="loading"
                                        :disabled="!isFormValid || loading || !acceptTerms">
                                        <span v-if="!loading">Create Account</span>
                                        <span v-else>Creating...</span>
                                    </v-btn>
                                </div>

                                <div class="login-link">
                                    <span>Has already an account?</span>
                                    <span class="login-text" @click="$router.push('/')">Sign in here</span>
                                </div>
                            </v-form>
                        </div>
                    </v-col>

                    <!-- Visual Section - Enhanced Discovery Theme -->
                    <v-col cols="12" lg="6" md="6" sm="12" class="visual-section">
                        <div class="visual-content">
                            <div class="feature-carousel">
                                <div class="feature-slide" :key="currentStep">
                                    <div class="feature-icon-wrapper">
                                        <v-icon size="48" color="white">{{ stepIcons[currentStep - 1] }}</v-icon>
                                    </div>
                                    <h3>{{ stepHeadings[currentStep - 1] }}</h3>
                                    <p>{{ stepDescriptions[currentStep - 1] }}</p>
                                </div>
                            </div>
                            <div class="benefits-container">
                                <div class="benefit-item">
                                    <span class="benefit-title">Free</span>
                                    <span class="benefit-label">Easy entry for your businesses. No barrier to
                                        start.</span>
                                </div>
                                <div class="stat-divider"></div>
                                <div class="benefit-item">
                                    <span class="benefit-title">Find</span>
                                    <span class="benefit-label">Let your customers discover your products.</span>
                                </div>
                                <div class="stat-divider"></div>
                                <div class="benefit-item">
                                    <span class="benefit-title">Feel</span>
                                    <span class="benefit-label">Experience connection, trust, and community
                                        support.</span>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-card>

            <!-- Shop Location Dialog -->
            <LocationConfirmDialog v-model="locationConfirmDialog" 
                @location-selected="handleLocationSelected" />

            <!-- Bottom Sheet -->
            <v-bottom-sheet v-model="termsSheetVisible">
                <v-card>
                    <v-card-title class="text-h5 pa-4 bg-warning">
                        Terms of Use
                    </v-card-title>

                    <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
                        <v-container class="bottom-sheet-container">
                            <div class="d-flex flex-column align-center mx-auto" width="100%" rounded>
                                <div class="text-left text-justify mb-4">
                                    <p>
                                        Please read these Terms of Use carefully while using the App operated by us.
                                        These Terms outline the rights and obligations between you and us regarding the
                                        use of the App.
                                    </p>
                                </div>

                                <!-- 1 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">1. Acceptance of Terms</h3>
                                    <p class="ml-3 text-justify">
                                        By accessing or using the App, you agree to be bound by these Terms and all
                                        applicable
                                        laws and regulations. If you do not agree to these Terms, please refrain from
                                        using the
                                        App.
                                    </p>
                                </div>

                                <!-- 2 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">2. Intellectual Property
                                    </h3>
                                    <p class="ml-3 text-justify">
                                        All content, including text, graphics, logos, images, available on the App,
                                        is our
                                        property or the property of my licensors and is protected by intellectual
                                        property laws.
                                        You may not reproduce, distribute, modify, display, or create derivative works
                                        of any
                                        content without our prior written consent.
                                    </p>
                                </div>

                                <!-- 3 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">3. Use of the App</h3>
                                    <p class="ml-3 mb-2 text-justify">
                                        a. <em>Eligibility:</em> You must be at least 15 years old to use the App.
                                        By using the
                                        App,
                                        you represent and warrant that you are of the required age.
                                    </p>
                                    <p class="ml-3 mb-2 text-justify">
                                        b. <em>Prohibited Conduct:</em> You agree not to engage in any of the following
                                        activities
                                        while
                                        using the App:
                                    </p>
                                    <ul style="list-style-type: none;" class="ml-7">
                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                i. Violating any applicable laws or regulations;
                                            </p>
                                        </li>

                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                ii. Infringing upon the rights of others;
                                            </p>
                                        </li>

                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                iii. Uploading or transmitting any harmful, offensive, or unlawful
                                                content;
                                            </p>
                                        </li>

                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                iv. Interfering with the operation of the App or other users' access
                                                to it;
                                            </p>
                                        </li>

                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                v. Attempting to gain unauthorized access to any part of the App or
                                                its systems;
                                            </p>
                                        </li>

                                        <li>
                                            <p class="ml-3 mb-2 text-justify">
                                                vi. Engaging in any conduct that could damage, disable, or impair the
                                                App or its
                                                functionality.
                                            </p>
                                        </li>
                                    </ul>
                                </div>

                                <!-- 4 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">4. Third-Party Content and
                                        Links</h3>
                                    <p class="ml-3 text-justify">
                                        The App may contain links to third-party Apps or resources. We do not
                                        endorse or
                                        assume any responsibility for the content, products, or services offered by
                                        third parties.
                                        Visiting any third-party Apps is at your own risk, and you should review the
                                        terms
                                        and privacy policies of such Apps.
                                    </p>
                                </div>

                                <!-- 5 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">5. Limitation of Liability
                                    </h3>
                                    <p class="ml-3 text-justify">
                                        To the maximum extent permitted by law, we shall not be liable for any indirect,
                                        incidental,
                                        consequential, or punitive damages arising out of or relating to the use or
                                        inability to use
                                        the App, even if we have been advised of the possibility of such damages. We
                                        total
                                        liability, whether in contract, warranty, tort (including negligence), or
                                        otherwise,
                                        shall not exceed the amount paid by you, if any, for accessing the App.
                                    </p>
                                </div>

                                <!-- 6 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">6. Indemnification</h3>
                                    <p class="ml-3 text-justify">
                                        You agree to indemnify, defend, and hold us harmless from and against any
                                        claims, damages,
                                        losses, liabilities, costs, or expenses arising out of or related to your use of
                                        the
                                        App or any violation of these Terms.
                                    </p>
                                </div>

                                <!-- 7 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">7. Modifications to the
                                        Terms</h3>
                                    <p class="ml-3 text-justify">
                                        We reserve the right to modify or replace these Terms at any time without prior
                                        notice.
                                        By continuing to access or use the App after such modifications, you agree
                                        to be bound
                                        by the updated Terms.
                                    </p>
                                </div>

                                <!-- 8 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">8. Severability</h3>
                                    <p class="ml-3 text-justify">
                                        If any provision of these Terms is found to be invalid or unenforceable, that
                                        provision
                                        shall be limited or eliminated to the minimum extent necessary, and the
                                        remaining provisions
                                        shall remain in full force and effect.
                                    </p>
                                </div>

                                <!-- 9 -->
                                <div class="mb-2">
                                    <h3 class="mb-1">9. Entire Agreement</h3>
                                    <p class="ml-3 text-justify">
                                        These Terms constitute the entire agreement between you and us regarding the use
                                        of the
                                        App, superseding any prior agreements or understandings.
                                    </p>
                                </div>

                                <!-- Last -->
                                <div class="mt-4">
                                    <p class="ml-3 mb-1 text-justify">
                                        If you have any concerns regarding with these Terms, please reach us at your convinient
                                        time.
                                    </p>
                                    <p class="ml-3 text-justify">
                                        By using the App, you acknowledge that you have read, understood, and agreed
                                        to these
                                        Terms of
                                        Use.
                                    </p>
                                </div>
                            </div>
                        </v-container>
                    </v-card-text>

                    <v-card-actions class="pa-4">
                        <v-spacer></v-spacer>
                        <v-btn color="warning" variant="flat" prepend-icon="mdi-close-circle-outline" @click="termsSheetVisible = false">
                            Close
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-bottom-sheet>
        </v-container>
    </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { useAuthStore } from '@/stores/auth';
import LocationConfirmDialog from '@/components/Dialogs/LocationConfirmDialog.vue'

export default {
    name: 'RegisterPage',

    components: {
        LocationConfirmDialog,
    },

    data() {
        return {
            logo: require('@/assets/Logo.png'),
            locationConfirmDialog: false,
            currentStep: 1,
            totalSteps: 3,
            showPassword: false,
            showConfirmPassword: false,
            termsSheetVisible: false,
            acceptTerms: false,
            loading: false,
            isFormValid: false,
            formData: {
                shop_name: '',
                shop_type: '',
                shop_owner: '',
                shop_address: '',
                branch_latitude: '',
                branch_longitude: '',
                shop_email: '',
                shop_contact_number: '',
                open_at: '',
                close_at: '',
                admin_password: '',
                confirm_password: ''
            },
            validationErrors: {},

            steps: [
                { number: 1, label: 'Business Info' },
                { number: 2, label: 'Location & Hours' },
                { number: 3, label: 'Contact & Auth' }
            ],

            storeTypes: [
                'Restaurant', 'Coffee Shop', 'Cafe', 'Carinderia',
                'Bakery', 'Street Food', 'Dessert Shop', 'Food Stall',
                'Other'
            ],

            stepIcons: ['mdi-storefront-outline', 'mdi-map-marker-radius', 'mdi-account-check-outline'],
            stepHeadings: [
                'Tell us about your business',
                'Where and when can customers find you?',
                'Almost there! Create your account'
            ],
            stepDescriptions: [
                'Start by sharing your business identity. This helps customers recognize and trust your brand.',
                'Help hungry customers discover your location and know when you\'re open for business.',
                'Complete your registration by providing contact details and secure credentials.'
            ]
        };
    },

    setup() {
        const toast = useToast();

        return {
            toast,
        };
    },

    computed: {
        isStepValid() {
            if (this.currentStep === 1) {
                return !!(this.formData.shop_owner && this.formData.shop_name && this.formData.shop_type);
            }
            if (this.currentStep === 2) {
                return !!(this.formData.shop_address && this.formData.open_at && this.formData.close_at);
            }
            if (this.currentStep === 3) {
                return !!(this.formData.shop_contact_number && this.formData.shop_email &&
                    this.formData.admin_password && this.formData.confirm_password &&
                    this.formData.admin_password === this.formData.confirm_password);
            }
            return false;
        },
    },

    mounted() {
        //
    },

    methods: {
        // Time conversion methods
        formatTimeForDisplay(dbTime) {
            if (!dbTime) return '';
            // Handle time in format "07:00:00" or "07:00"
            const parts = dbTime.split(':');
            const hours = parts[0];
            const minutes = parts[1];
            const hour = parseInt(hours, 10);
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const hour12 = hour % 12 || 12;
            return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        },

        formatTimeForDB(displayTime) {
            if (!displayTime) return null;
            const match = displayTime.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
            if (!match) return null;

            let hour = parseInt(match[1], 10);
            const minute = match[2];
            const period = match[3].toUpperCase();

            if (period === 'PM' && hour !== 12) hour += 12;
            if (period === 'AM' && hour === 12) hour = 0;

            return `${hour.toString().padStart(2, '0')}:${minute}:00`;
        },

        requiredRule(v) {
            return !!v || 'This field is required';
        },

        max50Rule(v) {
            return (v && v.length <= 50) || 'Maximum 50 characters only';
        },

        emailFormatRule(v) {
            const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return pattern.test(v) || 'Please enter a valid email address';
        },

        mobileNumberRule(v) {
            const pattern = /^(\+63|0)[0-9]{10}$/;
            return pattern.test(v.replace(/\s/g, '')) || 'Enter a valid Philippine mobile number (e.g., 09123456789 or +639123456789)';
        },

        timeFormatRule(v) {
            if (!v) return true; // Let requiredRule handle empty values
            const pattern = /^(0?[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/i;
            return pattern.test(v) || 'Use format: 07:00 AM or 08:00 PM';
        },

        passwordRule(v) {
            return v.length >= 8 || 'Password must be at least 8 characters';
        },

        confirmPasswordRule() {
            return this.formData.admin_password === this.formData.confirm_password || 'Passwords do not match';
        },

        nextStep() {
            if (this.isStepValid) {
                this.currentStep++;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                this.toast.error('Please fill in all required fields before proceeding');
            }
        },

        prevStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },

        goToStep(step) {
            if (step < this.currentStep) {
                this.currentStep = step;
            } else if (step > this.currentStep && this.isStepValid) {
                this.currentStep = step;
            } else if (step > this.currentStep) {
                this.toast.error('Please complete current step first.');

            }
        },

        showLocationConfirmDialog() {
            this.locationConfirmDialog = true;
        },

        handleLocationSelected(locationData) {
            if (locationData && locationData.address) {
                if (locationData && locationData.address && locationData.coordinates) {
                    this.formData.shop_address = locationData.address;
                    this.formData.branch_latitude = locationData.coordinates.lat;
                    this.formData.branch_longitude = locationData.coordinates.lng;
                }
                this.toast.success('Location address has been set!');
            }
        },

        async handleRegister() {
            const isValid = await this.$refs.form.validate();
            if (!isValid.valid) return;

            this.loading = true;
            try {
                await new Promise(resolve => setTimeout(resolve, 1500));

                const authStore = useAuthStore();

                // Prepare data for submission - convert time formats
                const submissionData = {
                    ...this.formData,
                    open_at: this.formatTimeForDB(this.formData.open_at),
                    close_at: this.formatTimeForDB(this.formData.close_at)
                };

                // Remove confirm_password as it's not needed in the backend
                delete submissionData.confirm_password;

                const result = await authStore.shopRegistration(submissionData);

                if (result && result.success === true) {
                    this.toast.success(result.message);
                    
                    // Force a small delay and ensure auth state is ready
                    setTimeout(() => {
                        // Use router.push instead of window.location.href
                        this.$router.push('/branch/Main').catch(err => {
                            console.error('Navigation error:', err);
                            // Fallback to window.location if router fails
                            window.location.href = '/branch/Main';
                        });
                    }, 500);
                } else {
                    this.handleValidationErrors(result.errors);
                }

            } catch (error) {
                console.error(error);

                if (error.response) {
                    const status = error.response.status;
                    const data = error.response.data;

                    if (status === 422 && data.errors) {
                        this.handleValidationErrors(data.errors);
                        // this.toast.error(data.message || 'Validation failed');
                        this.toast.error(Object.values(data.errors)[0][0]);
                    } else if (status === 500) {
                        this.toast.error(data.message || 'Server error occurred');
                    } else {
                        this.toast.error(data.message || 'Registration failed');
                    }
                } else if (error.request) {
                    this.toast.error('Network error. Please check your connection.');
                } else {
                    this.toast.error(error.message || 'An unexpected error occurred');
                }
            } finally {
                this.loading = false;
            }
        },

        handleValidationErrors(errors) {
            if (this.$refs.form && this.$refs.form.setErrors) {
                this.$refs.form.setErrors(errors);
            }
            this.validationErrors = errors;

            // for (const [field, messages] of Object.entries(errors)) {
            //     const errorMessage = messages.join(', ');
            //     this.toast.error(`${field}: ${errorMessage}`);
            // }
        },

        openTermsSheet() {
            this.termsSheetVisible = true
        },

    }
};
</script>

<style scoped>
.bottom-sheet-container h3, .bottom-sheet-container p {
    color: #444444;
}

.register-container {
    min-height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    background: linear-gradient(135deg, #faf8f5 0%, #fff6e3 100%);
    overflow-x: hidden;
    padding: 40px 0;
}

/* Animated Background */
.animated-bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    z-index: 0;
}

.gradient-sphere {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.5;
    animation: float 20s infinite ease-in-out;
}

.sphere-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(212, 162, 0, 0.3) 0%, rgba(180, 126, 0, 0.1) 100%);
    top: -200px;
    left: -200px;
    animation-delay: 0s;
}

.sphere-2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(220, 147, 0, 0.25) 0%, rgba(180, 126, 0, 0.08) 100%);
    bottom: -100px;
    right: -100px;
    animation-delay: -5s;
}

.sphere-3 {
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(255, 162, 0, 0.2) 0%, rgba(180, 108, 0, 0.05) 100%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    animation-delay: -10s;
}

/* Register Card */
.register-card {
    border-radius: 32px;
    overflow: hidden;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    max-width: 1200px;
    margin: 0 auto;
}

/* Form Section */
.form-section {
    padding: 40px 32px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
}

.form-content {
    max-width: 480px;
    margin: 25px;
}

.form-content h1 {
    font-size: 1.8rem;
    font-weight: 500;
    text-align: center;
    margin: 6px 0 0;
    width: 100%;
    color: #000;
}

/* Logo Animation */
.logo-wrapper {
    position: relative;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.logo-img {
    width: 100%;
    max-width: 200px;
    height: auto;
    position: relative;
    z-index: 1;
    transition: transform 0.3s ease;
}

.brand-title {
    background: linear-gradient(135deg, #5c3a21 0%, #d46600 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 800;
}

.subtitle {
    text-align: center;
    color: #a2a2a2;
    font-size: 0.85rem;
    margin-bottom: 24px;
}

/* Step Indicator */
.step-indicator {
    display: flex;
    justify-content: space-between;
    margin-bottom: 32px;
    position: relative;
}

.step-indicator::before {
    content: '';
    position: absolute;
    top: 16px;
    left: 0;
    right: 0;
    height: 2px;
    background: #e0e0e0;
    z-index: 0;
}

.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    cursor: pointer;
    flex: 1;
}

.step-circle {
    width: 34px;
    height: 34px;
    background: white;
    border: 2px solid #e0e0e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    color: #999;
    transition: all 0.3s ease;
    margin-bottom: 8px;
}

.step-item.active .step-circle {
    background: linear-gradient(135deg, #5c3a21 0%, #d46600 100%);
    border-color: transparent;
    color: white;
}

.step-item.completed .step-circle {
    background: #4caf50;
    border-color: #4caf50;
    color: white;
}

.step-label {
    font-size: 0.7rem;
    font-weight: 500;
    color: #999;
    transition: color 0.3s ease;
}

.step-item.active .step-label {
    color: #d46600;
    font-weight: 600;
}

.step-item.completed .step-label {
    color: #4caf50;
}

/* Step Content */
.step-content {
    animation: fadeIn 0.4s ease-out;
}

/* Form Elements */
.register-form {
    margin-top: 8px;
}

.input-wrapper {
    margin-bottom: 4px;
}

.input-label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #50452c;
}

.label-icon {
    color: #5c3a21;
}

:deep(.v-selection-control__input > .v-icon) {
    color:#444444
}

:deep(.v-field__input) {
    color: #444444;
}

.custom-input :deep(input::placeholder) {
    color: #a0a0a0;
}

.custom-input :deep(.v-field) {
    border-radius: 12px;
    transition: all 0.2s ease;
    border: 1px solid #ccc;
}

.custom-input :deep(.v-field:hover) {
    border-color: #5c3a21;
}

.custom-input :deep(.v-field--focused) {
    border-color: #5c3a21;
    box-shadow: 0 0 0 2px rgba(182, 112, 0, 0.15);
}


/* Navigation Buttons */
.navigation-buttons {
    display: flex;
    justify-content: space-between;
    gap: 5px;
}

.nav-btn {
    width: 50%;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0.3px;
    border-radius: 40px;
    height: 48px;
}

.prev-btn {
    border-color: #d46600;
    color: #d46600;
}

.prev-btn:hover {
    background: rgba(212, 102, 0, 0.05);
    border-color: #5c3a21;
}

.next-btn,
.register-btn {
    background: linear-gradient(135deg, #5c3a21 0%, #d46600 100%);
    box-shadow: 0 4px 12px rgba(212, 102, 0, 0.3);
}

.next-btn:hover,
.register-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(212, 102, 0, 0.4);
}

.login-link {
    text-align: center;
    margin-top: 24px;
    font-size: 0.85rem;
    color: #6c7a8a;
}

.login-text {
    color: #d46600;
    font-weight: 600;
    cursor: pointer;
    margin-left: 5px;
    transition: color 0.2s ease;
}

.login-text:hover {
    color: #5c3a21;
    text-decoration: underline;
}

/* Visual Section */
.visual-section {
    background: linear-gradient(135deg, #5c3a21 0%, #d46600 100%);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;
}

.visual-section::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" opacity="0.1"><path fill="white" d="M20,20 L30,20 L25,30 Z M70,30 L80,30 L75,40 Z M40,70 L50,70 L45,80 Z M80,80 L90,80 L85,90 Z M10,60 L20,60 L15,70 Z M90,10 L100,10 L95,20 Z"/><circle cx="50" cy="50" r="8"/><circle cx="30" cy="70" r="5"/><circle cx="70" cy="30" r="5"/></svg>') repeat;
    background-size: 30px;
    animation: movePattern 30s linear infinite;
}

.visual-content {
    position: relative;
    z-index: 2;
    padding: 40px;
    text-align: center;
    color: white;
}

.feature-carousel {
    margin-bottom: 48px;
}

.feature-slide {
    animation: fadeInUp 0.5s ease-out;
}

.feature-icon-wrapper {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(10px);
    transition: transform 0.3s ease;
}

.feature-slide h3 {
    font-size: 1.6rem;
    font-weight: 500;
    margin-bottom: 16px;
    color: #f6f6f6;
}

.feature-slide p {
    font-size: 0.9rem;
    opacity: 0.9;
    max-width: 480px;
    margin: 0 auto;
    line-height: 1.5;
    color: #ffffff;
}

.benefits-container {
    display: flex;
    justify-content: center;
    align-items: start;
    gap: 24px;
    margin-top: 48px;
    padding-top: 32px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
}

.benefit-item {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.benefit-title {
    font-size: 1.6rem;
    font-weight: 800;
    line-height: 1.2;
}

.benefit-label {
    font-size: 0.7rem;
    opacity: 0.8;
    letter-spacing: 0.3px;
    text-align: center;
    max-width: 100px;
}

.stat-divider {
    width: 1px;
    height: 40px;
    background: rgba(255, 255, 255, 0.3);
}

.terms-checkbox p {
    font-size: 14px !important;
}

.terms-link {
    margin-left: 1px;
    color: rgb(var(--v-theme-primary));
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;
}

.terms-link:hover {
    opacity: 0.8;
}

/* Optional: Remove padding from checkbox */
:deep(.v-checkbox .v-selection-control) {
    padding: 0 !important;
}

.no-padding-checkbox {
    padding: 0 !important;
}

/* Remove inner padding/margin from the input wrapper */
.no-padding-checkbox :deep(.v-input__control) {
    padding: 0 !important;
}

.no-padding-checkbox :deep(.v-selection-control) {
    padding: 0 !important;
    margin: 0 !important;
}

/* Animations */
@keyframes float {

    0%,
    100% {
        transform: translate(0, 0) scale(1);
    }

    33% {
        transform: translate(30px, -40px) scale(1.1);
    }

    66% {
        transform: translate(-20px, 30px) scale(0.95);
    }
}

@keyframes movePattern {
    0% {
        background-position: 0 0;
    }

    100% {
        background-position: 100px 100px;
    }
}

@keyframes blink {

    0%,
    100% {
        opacity: 1;
        transform: scale(1);
    }

    50% {
        opacity: 0.5;
        transform: scale(0.8);
    }
}

@keyframes pulse {

    0%,
    100% {
        transform: scale(1);
        opacity: 0.5;
    }

    50% {
        transform: scale(1.2);
        opacity: 0.8;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateX(10px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Responsive */
@media (max-width: 960px) {
    .form-section {
        padding: 32px 24px;
    }

    .visual-section {
        min-height: 400px;
    }

    .feature-slide h3 {
        font-size: 1.3rem;
    }

    .benefit-title {
        font-size: 1.3rem;
    }
}

@media (max-width: 600px) {
    .form-content h1 {
        font-size: 1.5rem;
    }

    .step-label {
        font-size: 0.6rem;
    }

    .step-circle {
        width: 28px;
        height: 28px;
        font-size: 0.8rem;
    }

    .benefits-container {
        gap: 12px;
    }

    .benefit-title {
        font-size: 1.1rem;
    }

    .benefit-label {
        font-size: 0.6rem;
        max-width: 80px;
    }

    .feature-icon-wrapper {
        width: 60px;
        height: 60px;
    }

    .navigation-buttons {
        flex-direction: column;
    }

    .nav-btn {
        width: 100%;
    }
}

.cursor-pointer {
    cursor: pointer;
}

.row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -8px;
}

.col-6 {
    flex: 0 0 50%;
    padding: 0 8px;
}
</style>